import { useSyncExternalStore } from 'react'

// fixed_header: a list panel's view controls (its scope tabs and its sort
// order), published by the controls in the list's own header card so the
// panel's top bar can offer the same switches once that card has scrolled
// away. Keyed by panel id; pure state, no rendering.

export type ViewOption = {
    key: string
    label: string
    // A lucide icon component, or a Font Awesome class string.
    icon?: unknown
}

export type ViewControl = {
    options: ViewOption[]
    active: string | null
    select: (key: string) => void
}

export type PanelViewControls = {
    // The list's own name over its tabs ("Home", "Channels"…).
    title?: string
    tabs?: ViewControl | null
    order?: ViewControl | null
}

const controls = new Map<string, PanelViewControls>()
const listeners = new Set<() => void>()

function emit() {
    listeners.forEach(listener => listener())
}

function sameControl(a?: ViewControl | null, b?: ViewControl | null) {
    if (!a || !b) return a === b
    return (
        a.active === b.active &&
        a.options.length === b.options.length &&
        a.options.every((o, i) => o.key === b.options[i].key && o.label === b.options[i].label)
    )
}

// Merge one part (title/tabs/order) for a panel. Handlers are refreshed in
// place; listeners only hear about changes they would render.
export function setPanelViewControls(panelId: string, part: Partial<PanelViewControls>) {
    if (!panelId) return
    const prev = controls.get(panelId) || {}
    const next = { ...prev, ...part }
    const changed =
        prev.title !== next.title ||
        !sameControl(prev.tabs, next.tabs) ||
        !sameControl(prev.order, next.order)
    controls.set(panelId, changed ? next : Object.assign(prev, part))
    if (changed) emit()
}

export function clearPanelViewControls(panelId: string, key: keyof PanelViewControls) {
    const prev = controls.get(panelId)
    if (!prev || prev[key] == null) return
    const next = { ...prev }
    delete next[key]
    controls.set(panelId, next)
    emit()
}

export function getPanelViewControls(panelId: string | null | undefined) {
    return (panelId && controls.get(panelId)) || null
}

function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

export function usePanelViewControls(panelId: string | null | undefined) {
    return useSyncExternalStore(
        subscribe,
        () => getPanelViewControls(panelId),
        () => null,
    )
}
